import type {
  AmendOptionInput,
  AvatarBoClient,
  BoCatalog,
  BoOptionAmendResult,
  BoSectionUpsertResult,
  BoSetSummary,
  BoSnapshotProfile,
  BoTierBands,
  BoVersion,
  ConfigDocument,
  CreateSetInput,
  GateIssue,
  ManifestDocument,
  OptionRef,
  OptionStatus,
  PresignedUpload,
  ProgressFn,
  RegisterOptionInput,
  SnapshotProfileInput,
  SwatchRef,
  UpsertSectionInput,
} from './types'

export class AvatarBoError extends Error {
  readonly status: number
  readonly code: string
  readonly issues: GateIssue[]

  constructor(
    status: number,
    code: string,
    issues: GateIssue[],
    message: string,
  ) {
    super(message)
    this.name = 'AvatarBoError'
    this.status = status
    this.code = code
    this.issues = issues
  }
}

export interface AvatarBoClientOpts {
  apiBaseUrl?: string
  authToken?: string | (() => string | undefined)
  /** The config route takes the org from `x-tenant-id`, not the token. Preview needs it. */
  orgId?: string | (() => string | undefined)
  fetchImpl?: typeof fetch
  uploadImpl?: UploadFn
}

export type UploadFn = (
  upload: PresignedUpload,
  file: File,
  onProgress?: ProgressFn,
) => Promise<void>

export function createAvatarBoClient(
  opts: AvatarBoClientOpts = {},
): AvatarBoClient {
  const base = (opts.apiBaseUrl ?? '').replace(/\/$/, '')
  if (!base) {
    throw new Error('Avatar studio: API base URL is not configured')
  }
  const f = opts.fetchImpl ?? fetch
  const upload = opts.uploadImpl ?? xhrUpload

  const currentToken = (): string | undefined =>
    typeof opts.authToken === 'function' ? opts.authToken() : opts.authToken

  async function req<T>(
    path: string,
    init?: Omit<RequestInit, 'body'> & { body?: unknown },
  ): Promise<T> {
    const token = currentToken()
    const hasBody = init?.body !== undefined
    const res = await f(`${base}${path}`, {
      ...init,
      body: hasBody ? JSON.stringify(init?.body) : undefined,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers as Record<string, string> | undefined),
      },
    })

    if (!res.ok) {
      throw await asError(res)
    }
    if (res.status === 204) {
      return undefined as unknown as T
    }

    const body = (await res.json()) as unknown

    return unwrapEnvelope<T>(body)
  }

  const currentOrg = () =>
    typeof opts.orgId === 'function' ? opts.orgId() : opts.orgId

  return {
    // The BO route, not the player config: the manifest keeps tier rules, paint order and tints,
    // and the org comes from the token like every other BO call.
    getVersionManifest: (n: number) =>
      req<ManifestDocument>(`/v1/bo/versions/${n}/manifest`),
    // Tier 5 so nothing is withheld: only fetched for its resolved layer URLs.
    getVersionConfig: async (setId: string, n: number) => {
      const org = currentOrg()
      const res = await f(
        `${base}/v1/config/v${n}/${encodeURIComponent(setId)}?tier=5`,
        { headers: org ? { 'x-tenant-id': org } : {} },
      )
      if (!res.ok) {
        throw await asError(res)
      }

      return (await res.json()) as ConfigDocument
    },
    listSets: () => req<BoSetSummary[]>('/v1/bo/sets'),
    getTierBands: () => req<BoTierBands>('/v1/bo/tier-bands'),
    setTierBands: (bands: number[]) =>
      req<{ bands: number[] }>('/v1/bo/tier-bands', {
        method: 'PUT',
        body: { bands },
      }),
    getSnapshotProfile: () => req<BoSnapshotProfile>('/v1/bo/snapshot-profile'),
    setSnapshotProfile: (input: SnapshotProfileInput) =>
      req<SnapshotProfileInput>('/v1/bo/snapshot-profile', {
        method: 'PUT',
        body: input,
      }),
    getCatalogue: (setId) =>
      req<BoCatalog>(`/v1/bo/sets/${encodeURIComponent(setId)}/catalog`),
    createSet: (input: CreateSetInput) =>
      req<{ id: string; name: string }>('/v1/bo/sets', {
        method: 'POST',
        body: input,
      }),
    upsertSection: (input: UpsertSectionInput) =>
      req<BoSectionUpsertResult>('/v1/bo/categories', {
        method: 'POST',
        body: input,
      }),
    presignUpload: (uploadId, filename) =>
      req<PresignedUpload>('/v1/bo/uploads', {
        method: 'POST',
        body: { uploadId, filename },
      }),
    putToStaging: (presigned, file, onProgress) =>
      upload(presigned, file, onProgress),
    registerOption: (input: RegisterOptionInput) =>
      req<unknown>('/v1/bo/options', { method: 'POST', body: input }),
    retireOption: (ref: OptionRef) =>
      req<OptionRef & { status: OptionStatus }>('/v1/bo/options/retire', {
        method: 'POST',
        body: ref,
      }),
    restoreOption: (ref: OptionRef) =>
      req<OptionRef & { status: OptionStatus }>('/v1/bo/options/restore', {
        method: 'POST',
        body: ref,
      }),
    // The colour pair. Same shape as the option pair on purpose: withdrawing a colour is the same
    // decision as withdrawing artwork, and the API refuses to let either be deleted outright.
    retireSwatch: (ref: SwatchRef) =>
      req<SwatchRef & { status: OptionStatus }>('/v1/bo/colours/retire', {
        method: 'POST',
        body: ref,
      }),
    restoreSwatch: (ref: SwatchRef) =>
      req<SwatchRef & { status: OptionStatus }>('/v1/bo/colours/restore', {
        method: 'POST',
        body: ref,
      }),
    amendOption: (input: AmendOptionInput) =>
      req<BoOptionAmendResult>('/v1/bo/options', {
        method: 'PATCH',
        body: input,
      }),
    listVersions: (channel) =>
      req<BoVersion[]>(
        channel
          ? `/v1/bo/versions?channel=${encodeURIComponent(channel)}`
          : '/v1/bo/versions',
      ),
    stageVersion: (channel) =>
      req<BoVersion>('/v1/bo/versions', {
        method: 'POST',
        body: channel ? { channel } : {},
      }),
    approveVersion: (n, note) =>
      req<{ approver: string; note: string | null }>(
        `/v1/bo/versions/${n}/approve`,
        { method: 'POST', body: note ? { note } : {} },
      ),
    declineVersion: (n, note) =>
      req<{ approver: string; note: string | null }>(
        `/v1/bo/versions/${n}/decline`,
        { method: 'POST', body: note ? { note } : {} },
      ),
    publishVersion: (n, channel) =>
      req<unknown>(`/v1/bo/versions/${n}/publish`, {
        method: 'POST',
        body: channel ? { channel } : {},
      }),
  }
}

function unwrapEnvelope<T>(body: unknown): T {
  if (
    body &&
    typeof body === 'object' &&
    'data' in (body as Record<string, unknown>) &&
    '_metadata' in (body as Record<string, unknown>)
  ) {
    return (body as { data: T }).data
  }

  return body as T
}

async function asError(res: Response): Promise<AvatarBoError> {
  const text = await res.text().catch(() => '')
  let code = `http_${res.status}`
  let issues: GateIssue[] = []
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>
    const payload = (parsed.data as Record<string, unknown>) ?? parsed
    if (typeof payload.error === 'string') {
      code = payload.error
    }
    if (Array.isArray(payload.issues)) {
      issues = payload.issues as GateIssue[]
    }
  } catch {
    /* not JSON */
  }

  return new AvatarBoError(
    res.status,
    code,
    issues,
    `Avatar studio API ${res.status} ${code}`,
  )
}

const xhrUpload: UploadFn = (upload, file, onProgress) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', upload.url, true)
    Object.entries(upload.headers).forEach(([k, v]) =>
      xhr.setRequestHeader(k, v),
    )
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        onProgress?.(e.loaded, e.total)
      }
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(
            new AvatarBoError(
              xhr.status,
              'upload_failed',
              [],
              `Upload of ${file.name} failed: ${xhr.status}`,
            ),
          )
    xhr.onerror = () =>
      reject(
        new AvatarBoError(
          0,
          'upload_unreachable',
          [],
          `Upload of ${file.name} could not reach the bucket`,
        ),
      )
    xhr.send(file)
  })
